import * as _ from 'lodash';
import { useCallback, useContext } from 'react';

import { RouterContext, useQuery as useQueryParams } from '../../../contexts/RouterContext';
import type { FossabotPRSort, FossabotPRVcsState } from '../../../../shared/fossabot/common';

/** The page's filters as they come off the URL: the search, the state and the sort. */
type FossabotPrsQueryParams = {
  search?: string;
  state?: FossabotPRVcsState;
  sort?: FossabotPRSort;
};

/**
 * The sort applied when the URL carries no `sort`, which is also the one the
 * sort control reads out on a fresh page.
 *
 * It stays out of the URL, but it is still sent to the list request. Omitting it
 * does not mean "newest": `listFossabotPRs` passes an absent sort through as
 * undefined and `listDependencyUpgradePrs` strips undefined variables before the
 * request leaves Core, so the fossabot subgraph falls back to an ordering nothing
 * in this repo pins down. Sending it keeps the control from naming an order the
 * request never asked for.
 */
const DEFAULT_FOSSABOT_PR_SORT: FossabotPRSort = 'newest';

/*
 * The states available as filters on this page. Draft PRs are not created by
 * fossabot, so a stale or hand-edited draft filter reads as All.
 */
const PR_FILTER_STATES = new Set<string>(
  ['open', 'closed', 'merged'] satisfies FossabotPRVcsState[],
);

const isFilterState = (value: unknown): value is FossabotPRVcsState =>
  _.isString(value) && PR_FILTER_STATES.has(value);

/*
 * The orders the list endpoint accepts. Its validator is a Zod enum too, so an
 * order it does not know is a 400 rather than a quietly ignored param.
 */
const PR_SORTS = new Set<string>(['newest', 'oldest'] satisfies FossabotPRSort[]);

const isSort = (value: unknown): value is FossabotPRSort =>
  _.isString(value) && PR_SORTS.has(value);

/*
 * Everything in a query string is a string, and a hand-edited URL can repeat a
 * param, which qs parses into an array. Narrowing here keeps an array out of the
 * input and out of the list request. Module-level so useQuery can memoize on it.
 */
const parseFossabotPrsQuery = (query: Record<string, unknown>): FossabotPrsQueryParams => ({
  search: _.isString(query.search) ? query.search : undefined,
  //  Anything that is not an available filter reads as "All", the same as no param.
  state: isFilterState(query.state) ? query.state : undefined,
  //  Anything that is not one of the two orders reads as no param, so the default applies.
  sort: isSort(query.sort) ? query.sort : undefined,
});

/**
 * Reads and writes the fossabot PRs page filters, which live in the URL so that
 * a filtered view can be linked and survives a reload.
 *
 * Setters spread the query read fresh out of `getQuery()` rather than the
 * render-time copy: `setQuery` replaces the whole query string, so anything not
 * spread back in is dropped, and reading it fresh stops two setters firing in
 * the same tick from clobbering each other.
 */
export function useFossabotPrsFilters() {
  const { getQuery } = useContext(RouterContext);
  const [queryParams, setQuery] = useQueryParams<FossabotPrsQueryParams>(parseFossabotPrsQuery);

  const setSearch = useCallback((search: string) => {
    const currentQuery = getQuery();
    /*
     * The search box can settle on the search the URL already has, say a character
     * typed and deleted again inside the debounce. Writing it back would replace
     * the whole query string, re-serializing every other param along the way.
     */
    if ((currentQuery.search ?? '') === search) {
      return;
    }
    //  qs drops undefined values, so this is how a cleared search leaves the URL.
    setQuery({ ...currentQuery, search: search || undefined });
  }, [getQuery, setQuery]);

  /*
   * Undefined is the "All" chip. qs drops undefined values, so passing it is how
   * the state param leaves the URL. Unlike the search there is no no-op guard:
   * this only runs on a click, never on mount.
   */
  const setState = useCallback((state?: FossabotPRVcsState) => {
    setQuery({ ...getQuery(), state });
  }, [getQuery, setQuery]);

  /*
   * The default sort collapses to undefined so that qs drops it: picking Newest
   * again has to leave the URL as clean as it was before anyone touched the
   * control. The default is still applied on the way out, so the request keeps
   * asking for it either way.
   */
  const setSort = useCallback((sort: FossabotPRSort) => {
    setQuery({ ...getQuery(), sort: sort === DEFAULT_FOSSABOT_PR_SORT ? undefined : sort });
  }, [getQuery, setQuery]);

  return {
    search: queryParams.search ?? '',
    setSearch,
    state: queryParams.state,
    setState,
    sort: queryParams.sort ?? DEFAULT_FOSSABOT_PR_SORT,
    setSort,
  };
}
